import { useId } from 'react'

import markOnLight from '../../assets/logo-mark-on-light.png'
import markReverse from '../../assets/logo-mark-reverse.png'
import { cn } from '../../lib/utils'
import { VIEW_H, VIEW_W, WRITE_LENGTH, WRITE_PATH, WRITE_STROKE } from './logo-loader-geometry'

/**
 * The parts the logo loader draws. `logo-loader.tsx` holds the component and
 * its camera; this file holds only markup, so neither crosses 300 lines.
 *
 * Every piece here is decorative. The loader root carries the label and the
 * colour, so nothing below declares a role or a text colour of its own.
 */

/** One theme-correct copy of the mark as plain images. Always decorative. */
function MarkPair({ size }: { size: number }) {
  // Exactly one of the two displays at any time - the same CSS swap <Logo>
  // uses, so the visible file always agrees with the surface's theme.
  return (
    <>
      <img
        src={markOnLight}
        alt=""
        height={size}
        style={{ height: size }}
        className="block w-auto dark:hidden"
      />
      <img
        src={markReverse}
        alt=""
        height={size}
        style={{ height: size }}
        className="hidden w-auto dark:block"
      />
    </>
  )
}

/** The same theme swap, inside the SVG. `mask` applies to both files. */
function MarkImages({ mask }: { mask: string }) {
  return (
    <>
      <image
        href={markOnLight}
        x={0}
        y={0}
        width={VIEW_W}
        height={VIEW_H}
        mask={mask}
        className="block dark:hidden"
      />
      <image
        href={markReverse}
        x={0}
        y={0}
        width={VIEW_W}
        height={VIEW_H}
        mask={mask}
        className="hidden dark:block"
      />
    </>
  )
}

/**
 * The one masking stroke that writes the mark on.
 *
 * White reveals, black hides. Round caps, so the pen touches down as a dot
 * rather than a flat edge; round joins, so the apex, the vee and the right
 * apex fill without a miter spike.
 *
 * `stroke-dasharray` puts one gap the length of the whole path behind one dash
 * the length of the whole path. `logo-write-stroke` in theme.css then pulls
 * the offset from that length down to 0, which walks the dash across the path
 * from the bottom-left foot to the bottom-right foot.
 */
function WriteStroke() {
  return (
    <path
      data-slot="logo-loader-stroke"
      d={WRITE_PATH}
      fill="none"
      stroke="#fff"
      strokeWidth={WRITE_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={`${WRITE_LENGTH} ${WRITE_LENGTH}`}
      className="animate-logo-write-stroke"
    />
  )
}

/** The written mark: one copy of the artwork, uncovered by the one stroke. */
function WrittenMark({ size }: { size: number }) {
  // useId returns colons, which are legal in a fragment but awkward in a
  // url() reference. Strip them so the mask resolves in every engine.
  const maskId = `logo-write-${useId().replace(/:/g, '')}`

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      height={size}
      width={(size * VIEW_W) / VIEW_H}
      style={{ height: size, width: (size * VIEW_W) / VIEW_H }}
      className="block"
    >
      {/* No <title>: the SVG is decorative and the wrapper carries the label.
          Biome's noSvgWithoutTitle accepts that once aria-hidden is set. */}
      <defs>
        <mask id={maskId} maskUnits="userSpaceOnUse" x={0} y={0} width={VIEW_W} height={VIEW_H}>
          <WriteStroke />
        </mask>
      </defs>
      <MarkImages mask={`url(#${maskId})`} />
    </svg>
  )
}

function Wordmark({ animated }: { animated: boolean }) {
  return (
    <span
      data-slot="logo-loader-wordmark"
      aria-hidden="true"
      className={cn(
        // The colour is on the loader root, not here. tailwind-merge cannot
        // tell the custom size token `text-h3` from the custom colour token
        // `text-foreground` - it drops one of the pair - so the two never
        // share a `cn` call.
        'text-h3 font-semibold leading-none tracking-[0.16em]',
        // It sweeps in out of the gap beside the mark, the moment the pen
        // finishes the mark. The sweep must start at the edge
        // nearest the mark, and RTL puts the mark on the other side - hence
        // the mirrored pair. LTR is the base class rather than an `ltr:`
        // variant, so a document that declares no dir still animates.
        animated && 'animate-logo-write-wordmark rtl:animate-logo-write-wordmark-rtl',
      )}
    >
      {/* biome-ignore lint/plugin: the brand wordmark, the same in every language */}
      MULTAQA <span className="text-muted-foreground font-medium">ERP</span>
    </span>
  )
}

export { MarkPair, Wordmark, WrittenMark }
